import type { SVGProps } from 'react'
import { cx } from '@/lib/cx'
import styles from './Icon.module.css'

/** Path data on a 24×24 grid, drawn with a 1.5 stroke, round caps and joins. */
const PATHS = {
  'arrow-up-right': ['M7 17 17 7', 'M8 7h9v9'],
  'arrow-down': ['M12 4v16', 'm6 14 6 6 6-6'],
  mic: [
    'M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Z',
    'M19 11a7 7 0 0 1-14 0',
    'M12 18v3',
  ],
  send: ['M21 3 10 14', 'm21 3-7 18-4-7-7-4 18-7Z'],
  close: ['M6 6l12 12', 'M18 6 6 18'],
  menu: ['M4 9h16', 'M4 15h16'],
  download: ['M12 4v11', 'm7 10 5 5 5-5', 'M5 20h14'],
  shuffle: ['M16 4h4v4', 'M4 20 20 4', 'M20 16v4h-4', 'm15 15 5 5', 'M4 4l5 5'],
} as const satisfies Record<string, readonly string[]>

/** Names accepted by {@link Icon}. */
export type IconName = keyof typeof PATHS

/** Props for {@link Icon}. */
export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  name: IconName
  /** Width and height. Default `'1em'`, so the icon follows the font size. */
  size?: number | string
  /** Accessible name. Without it the icon is decorative (`aria-hidden`). */
  title?: string
}

/** Inline SVG icon that inherits `currentColor`. Decorative unless `title` is set. */
export function Icon({ name, size = '1em', title, className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx(styles.icon, className)}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}
